import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import type {
  AiPrompt,
  AiPromptVersion,
  ActivateAiPromptVersionParams,
  CreateAiPromptVersionParams,
  DeleteAiPromptVersionParams,
  PayloadToken,
} from "@/types/ai-prompt";

const AI_PROMPTS_KEY = ["ai-prompts"] as const;
const aiPromptKey = (id: number) => ["ai-prompts", "show", id] as const;
const PAYLOAD_TOKENS_KEY = ["payload-tokens"] as const;

export const useGetAiPrompts = () =>
  useQuery<AiPrompt[]>({
    queryKey: AI_PROMPTS_KEY,
    queryFn: () => apiService.get<AiPrompt[]>("/ai-prompts"),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetPayloadTokens = () =>
  useQuery<PayloadToken[]>({
    queryKey: PAYLOAD_TOKENS_KEY,
    queryFn: () => apiService.get<PayloadToken[]>("/payload-tokens"),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetAiPrompt = (id: number | null) =>
  useQuery<AiPrompt>({
    queryKey: aiPromptKey(id as number),
    queryFn: () => apiService.get<AiPrompt>(`/ai-prompts/${id}`),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useCreateAiPromptVersion = (promptId: number) =>
  useMutation<AiPromptVersion, unknown, CreateAiPromptVersionParams>({
    mutationFn: (data) =>
      apiService.post<AiPromptVersion, CreateAiPromptVersionParams>(
        `/ai-prompts/${promptId}/versions`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_PROMPTS_KEY });
      queryClient.invalidateQueries({ queryKey: aiPromptKey(promptId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useActivateAiPromptVersion = () =>
  useMutation<unknown, unknown, ActivateAiPromptVersionParams>({
    mutationFn: ({ promptId, versionId }) =>
      apiService.post(`/ai-prompts/${promptId}/versions/${versionId}/activate`),
    onSuccess: (_data, { promptId }) => {
      queryClient.invalidateQueries({ queryKey: AI_PROMPTS_KEY });
      queryClient.invalidateQueries({ queryKey: aiPromptKey(promptId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useResetAiPrompt = () =>
  useMutation<unknown, unknown, number>({
    mutationFn: (promptId) =>
      apiService.post(`/ai-prompts/${promptId}/reset`),
    onSuccess: (_data, promptId) => {
      queryClient.invalidateQueries({ queryKey: AI_PROMPTS_KEY });
      queryClient.invalidateQueries({ queryKey: aiPromptKey(promptId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useDeleteAiPromptVersion = () =>
  useMutation<unknown, unknown, DeleteAiPromptVersionParams>({
    mutationFn: ({ promptId, versionId }) =>
      apiService.delete(`/ai-prompts/${promptId}/versions/${versionId}`),
    onSuccess: (_data, { promptId }) => {
      queryClient.invalidateQueries({ queryKey: AI_PROMPTS_KEY });
      queryClient.invalidateQueries({ queryKey: aiPromptKey(promptId) });
    },
    onError: (error) => handleApiError(error),
  });
